import { useEffect } from 'react';
import { createRoute, redirect } from '@tanstack/react-router';
import { rootRoute } from './root';
import { authClient, getSession, devSignIn, DEV_AUTH_BYPASS } from '@/lib/auth-client';
import { stopRum } from '@/lib/rum';
import { AppError } from '@/components/route-error';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

function Login() {
  // Landing on /login means signed-out (an explicit logout, or an expired session
  // redirected here). Stop any RUM session that survived an SPA-internal deauth
  // without a reload — the one real-deauth seam besides the sign-out handler.
  // No-op off a prod deploy (lib/rum.ts).
  useEffect(() => {
    stopRum();
  }, []);

  const onGoogle = () => {
    // Redirects to Google, then back to callbackURL once Better Auth has a session.
    void authClient.signIn.social({ provider: 'google', callbackURL: '/' });
  };

  // Local dev: no Google — sign in as the stub dev SDR and go to the cockpit. The
  // full reload re-runs the route guard, which now sees a (dev) session.
  const onDevSignIn = () => {
    devSignIn();
    window.location.href = '/';
  };

  return (
    <div className="flex min-h-dvh items-center justify-center p-6">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <CardTitle className="text-xl">Maestro · Dialer</CardTitle>
          <CardDescription>Sign in to start calling</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {DEV_AUTH_BYPASS ? (
            <>
              <Button className="w-full" onClick={onDevSignIn} data-testid="dev-signin">
                Continue as dev (local)
              </Button>
              <p className="text-muted-foreground text-center text-xs">
                Local dev — auth is off. Set <code>VITE_DISABLE_DEV_AUTH=true</code> for real Google
                sign-in.
              </p>
            </>
          ) : (
            <>
              <Button className="w-full" onClick={onGoogle}>
                Continue with Google
              </Button>
              <p className="text-muted-foreground text-center text-xs">
                Only <code>@throxy.com</code> Google accounts can sign in.
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  // Already signed in → straight to the cockpit.
  beforeLoad: async () => {
    const { data } = await getSession();
    if (data) throw redirect({ to: '/' });
  },
  component: Login,
  // The bare frame, since nothing here can offer a way onward that is not this
  // page. No report alongside it, unlike every other route: collection is
  // deliberately not running on the signed-out view, so it would be a no-op
  // dressed as coverage.
  errorComponent: AppError,
});
